import type { Role } from '@trafiku/contracts';
import {
  Bell,
  BellRing,
  Building2,
  Bus,
  CalendarClock,
  ChartColumnIncreasing,
  ClipboardList,
  Gauge,
  Kanban,
  KeyRound,
  LayoutDashboard,
  LogOut,
  MapPin,
  MapPinned,
  Megaphone,
  MessageSquareWarning,
  Newspaper,
  Route,
  Shapes,
  ShieldAlert,
  Signpost,
  Smartphone,
  Stethoscope,
  Store,
  Users,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { NavLink, Outlet } from 'react-router';
import { useAlertCount } from '@/lib/alerts';
import { useAuth } from '@/lib/auth';
import { t } from '@/lib/i18n';
import { LanguageSwitcher } from '../language-switcher';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  roles?: Role[];
  /** Shows the number of open alerts next to the label. */
  alertBadge?: boolean;
}
const sections: { title: string; items: NavItem[] }[] = [
  { title: '', items: [{ to: '/', label: t('Dashboard'), icon: LayoutDashboard }] },
  {
    title: t('Network'),
    items: [
      { to: '/arrangement', label: t('Arrangement'), icon: Kanban },
      { to: '/buses', label: t('Buses'), icon: Bus },
      { to: '/lines', label: t('Lines'), icon: Shapes },
      { to: '/directions', label: t('Directions'), icon: Route },
      { to: '/stations', label: t('Stations'), icon: MapPin },
      { to: '/schedules', label: t('Timetables'), icon: CalendarClock },
      { to: '/out-of-service-areas', label: t('Out-of-service areas'), icon: ShieldAlert },
      { to: '/interest-points', label: t('Points of interest'), icon: Building2, roles: ['admin'] },
    ],
  },
  {
    title: t('Monitoring'),
    items: [
      { to: '/alerts', label: t('Alerts'), icon: BellRing, alertBadge: true },
      { to: '/performance', label: t('Performance'), icon: Gauge },
      { to: '/data-health', label: t('Data health'), icon: Stethoscope },
    ],
  },
  {
    title: t('Passengers'),
    items: [
      { to: '/demand', label: t('Passenger demand'), icon: ChartColumnIncreasing, roles: ['admin'] },
      { to: '/planner', label: t('Route planner'), icon: Signpost },
      { to: '/news', label: t('News'), icon: Newspaper },
      { to: '/reports', label: t('Reports'), icon: MessageSquareWarning },
      { to: '/notifications', label: t('Push notifications'), icon: Bell },
    ],
  },
  {
    title: t('Advertising'),
    items: [
      { to: '/campaigns', label: t('Campaigns'), icon: Megaphone, roles: ['admin'] },
      { to: '/advertisements', label: t('Station ads'), icon: Store, roles: ['admin'] },
      { to: '/pin-advertisements', label: t('Map pins'), icon: MapPinned, roles: ['admin'] },
    ],
  },
  {
    title: t('System'),
    items: [
      { to: '/mobile-app', label: t('Mobile app'), icon: Smartphone },
      { to: '/users', label: t('Admin users'), icon: Users, roles: ['admin'] },
      { to: '/audit-log', label: t('Audit log'), icon: ClipboardList, roles: ['admin'] },
      { to: '/account', label: t('My account'), icon: KeyRound },
    ],
  },
];

export function AppLayout() {
  const { user, logout, can } = useAuth();
  return (
    <div className="flex h-full">
      <aside className="flex w-60 shrink-0 flex-col bg-brand-900 text-slate-200">
        <div className="flex h-16 items-center gap-2 border-b border-white/10 px-4">
          <img src="/brand/logo-white.png" alt="Trafiku Urban Prishtine" className="h-9 w-auto" />
          <span className="ml-auto rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold tracking-wider text-slate-300 uppercase">
            Admin
          </span>
        </div>
        <nav className="flex-1 space-y-4 overflow-y-auto px-2 py-3">
          {sections.map((section) => {
            const items = section.items.filter((i) => !i.roles || can(...i.roles));
            if (!items.length) return null;
            return (
              <div key={section.title}>
                {section.title && (
                  <div className="px-2 pb-1 text-[11px] font-semibold tracking-wider text-urban uppercase">
                    {section.title}
                  </div>
                )}
                {items.map(({ to, label, icon: Icon, alertBadge }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === '/'}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 rounded-md border-l-2 px-2 py-1.5 text-sm ${isActive ? 'border-accent bg-white/10 font-medium text-white' : 'border-transparent text-slate-300 hover:bg-white/5 hover:text-white'}`
                    }
                  >
                    <Icon className="size-4" />
                    {label}
                    {alertBadge && <AlertBadge />}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="border-t border-white/10 p-3">
          <div className="truncate text-sm font-medium text-white">{user?.email}</div>
          <div className="mb-2 text-xs text-slate-400">
            {user?.role === 'admin' ? t('Administrator') : t('Operator')}
          </div>
          <button
            type="button"
            onClick={logout}
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-accent"
          >
            <LogOut className="size-4" /> {t('Sign out')}
          </button>
          <div className="mt-3 border-t border-white/10 pt-3">
            <LanguageSwitcher tone="dark" />
          </div>
        </div>
      </aside>
      <main className="min-w-0 flex-1 overflow-y-auto">
        <div className="px-6 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function AlertBadge() {
  const open = useAlertCount().data?.open ?? 0;
  if (!open) return null;
  return (
    <span className="ml-auto rounded-full bg-red-600 px-1.5 text-[11px] leading-[18px] font-semibold text-white">
      {open}
    </span>
  );
}
